import React, { useEffect, useContext, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RealtimeAction, RealtimeMessage, ActionType, TopicType } from './types';
import { waitForSocketConnection } from './wsUtils';
import { addTransportInfoArray, setTransportInfo, clearTransport } from 'app/actions';
import { addSubscription, removeSubscription } from 'app/actions/subscriptions';
import camelCaseKeys from 'camelcase-keys';
import { getEnvUrl, getSessionToken } from '../config';

const API_HOST =
	process.env.NODE_ENV === 'development'
		? `wss://${getEnvUrl()}vezzer.no/geo`
		: window.location.origin === 'https://app.dev.vezzer.no'
		? 'wss://dev.vezzer.no/geo'
		: 'wss://vezzer.no/geo';
// const API_HOST = 'wss://vezzer.no/geo';
console.log('--------ENV', process.env.DEV_CONNECT_TO);
export const WsContext = React.createContext<any>({
	subscribes: {},
	subscribe: () => {},
	unsubscribe: () => {},
});
const socket = new WebSocket(API_HOST);

const messageStatus = {
	STARTED: 'started',
	COMPLETED: 'completed',
};

const WsProvider = ({ children }: any) => {
	const sessionToken = useSelector((state: any) => state?.user?.sessionToken);
	const subscriptions = useSelector((state: any) => state?.subscriptions);
	const messagesPull = useRef([]);
	const queueStarted = useRef(false);
	const dispatch = useDispatch();
	// const sessionToken = 'fc543faa019ff834d0521f19ac9801a8e92b7994';
	const onOpen = () => {
		const authAction = {
			token: getSessionToken(sessionToken),
			action: 'auth',
		};
		console.log('openening ws connection');
		socket.send(JSON.stringify(authAction));
	};
	const onMessage = (event: any) => {
		if (event.data) {
			const message: RealtimeMessage = JSON.parse(event.data);

			if (message.notification) {
				console.log('Received notify: ', message.notification);

				if (message?.status === messageStatus.COMPLETED) {
					dispatch(addTransportInfoArray([...messagesPull.current]));
					messagesPull.current = [];
					queueStarted.current = false;
				}
				if (message?.status === messageStatus.STARTED) {
					queueStarted.current = true;
				}
			} else if (message.error) {
				console.error('Received error: ' + message.error);
			} else if (message.data) {
				const data = camelCaseKeys(message.data, { deep: true });

				if (queueStarted.current) {
					//@ts-ignore
					messagesPull.current = [...messagesPull.current, { ...data, topics: message.topics }];
				} else {
					dispatch(setTransportInfo({ ...data, topics: message.topics }));
				}
			}
		}
	};
	useEffect(() => {
		console.log('initializing socket');
		socket.onopen = onOpen;
		socket.onmessage = onMessage;
		return () => {
			console.log('closing ws connection');
			socket.close();
		};
	}, []);

	const onSubscribe = (topic: any) => {
		if (!subscriptions.find((item: any) => item === topic)) {
			const action: RealtimeAction = {
				action: ActionType.Subscribe,
				topic,
			};

			dispatch(addSubscription(topic));
			waitForSocketConnection(socket, (socket: any) => {
				console.log(`message for subscribe to ${topic} send!`);
				socket.send(JSON.stringify(action));
			});
		}
	};

	const onUnsubscribe = (topic: TopicType) => {
		const action: RealtimeAction = {
			action: ActionType.Unsubscribe,
			topic,
		};

		dispatch(removeSubscription(topic));
		dispatch(clearTransport());

		waitForSocketConnection(socket, (socket: any) => {
			console.log(`message for unsubscribe to ${topic} send!`);
			socket.send(JSON.stringify(action));
		});
	};

	return (
		<WsContext.Provider
			value={{
				subscribe: onSubscribe,
				unsubscribe: onUnsubscribe,
			}}
		>
			{children}
		</WsContext.Provider>
	);
};

export default WsProvider;

export const useWebSocket = () => {
	const { subscribe, unsubscribe, subscribes } = useContext(WsContext);
	return { subscribe, unsubscribe, subscribes };
};
